import React, { useEffect, useRef, useState } from 'react';
import {
  Text,
  View,
  Image,
  Pressable,
  StyleSheet,
  Dimensions,
  SafeAreaView,
  Animated,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RootStackParamList } from '../navigation/stackTypes';
import { isIPadSync, getResponsiveDimensions, getIPadSafeAreaAdjustments } from '../utils/ipad-utils';
import { navigationActions } from '../store/slices/navigation-slice';
import RoleService from '../services/role-service';
import { accessibleTextProps } from '../utils/accessibility-utils';

const { width, height } = Dimensions.get('window');
const ICON_SIZE = { width: 28, height: 28 }; // Reduced from 32 to 28
const ACTIVE_TINT = '#40BFB4';
const DEFAULT_TINT = '#FFF';
const SOFT_BLACK = '#181A1B';
const FLASH_COLOR = '#39BEB7';

import carIcon from '../../assets/footerIcons/find-a-charger.png';
import pumpIcon from '../../assets/footerIcons/chargers-nav-icon.png';
import profileIcon from '../../assets/footerIcons/my-account-nav-icon.png';
import historyIcon from '../../assets/footerIcons/charging-history-icon.png';

const icons = { car: carIcon, pump: pumpIcon, profile: profileIcon };

interface ButtonConfig {
  name: string;
  route: string;
  label: string;
  image: any;
  params?: object;
  disabled?: boolean;
}

const Footer = ({ currentRouteName }: { currentRouteName: string }) => {
  const [activeTab, setActiveTab] = useState(currentRouteName);
  const session = useSelector((state: RootState) => state.user.session);
  const currentChargerSelectionTab = useSelector(
    (state: RootState) => (state as any).navigation?.currentChargerSelectionTab || 'Map'
  );
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const dispatch = useDispatch();
  const isGuest = RoleService.isGuest(session);
  const primaryUse = session?.primary_use;
  const scaleAnim = useRef(new Animated.Value(1)).current;
  const flashAnim = useRef(new Animated.Value(1)).current;
  const backgroundColorAnim = useRef(new Animated.Value(0)).current;
  const footerScaleAnim = useRef(new Animated.Value(1)).current;
  const isFirstRender = useRef(true);
  
  // iPad detection and responsive dimensions
  const isIPadDevice = isIPadSync();
  const responsiveDimensions = getResponsiveDimensions();
  const ipadAdjustments = getIPadSafeAreaAdjustments();
  


  // useEffect(() => {
  //   if (isFirstRender.current) {
  //     isFirstRender.current = false;
  //     return;
  //   }
  //   Animated.parallel([
  //     Animated.sequence([
  //       Animated.timing(backgroundColorAnim, {
  //         toValue: 1,
  //         duration: 150,
  //         useNativeDriver: true,
  //       }),
  //       Animated.timing(backgroundColorAnim, {
  //         toValue: 0,
  //         duration: 150,
  //         useNativeDriver: true,
  //       }),
  //       Animated.timing(backgroundColorAnim, {
  //         toValue: 1,
  //         duration: 150,
  //         useNativeDriver: true,
  //       }),
  //       Animated.timing(backgroundColorAnim, {
  //         toValue: 0,
  //         duration: 150,
  //         useNativeDriver: true,
  //       }),
  //     ]),
  //     Animated.sequence([
  //       Animated.timing(flashAnim, {
  //         toValue: 0.3,
  //         duration: 150,
  //         useNativeDriver: true,
  //       }),
  //       Animated.timing(flashAnim, {
  //         toValue: 1,
  //         duration: 150,
  //         useNativeDriver: true,
  //       }),
  //     ]),
  //     Animated.sequence([
  //       Animated.timing(footerScaleAnim, {
  //         toValue: 1.2,
  //         duration: 150,
  //         useNativeDriver: true,
  //       }),
  //       Animated.timing(footerScaleAnim, {
  //         toValue: 1,
  //         duration: 150,
  //         useNativeDriver: true,
  //       }),
  //     ]),
  //   ]).start();
  // }, [primaryUse]);

  // Update activeTab when the currentRouteName prop changes
  useEffect(() => {
    setActiveTab(currentRouteName);
  }, [currentRouteName]);

  const handlePress = (route: string, params?: object) => {
    setActiveTab(route);
    Animated.sequence([
      Animated.timing(scaleAnim, {
        toValue: 1.2,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.timing(scaleAnim, {
        toValue: 1,
        duration: 100,
        useNativeDriver: true,
      }),
    ]).start();
    
    if (route === 'ChargerSelection' && params) {
      navigation.navigate('ChargerSelection', params as any);
    } else {
      navigation.navigate(route as any);
    }
  };

  const backgroundColor = backgroundColorAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ['#FAFAFA', FLASH_COLOR],
  });

  const buttons: ButtonConfig[] =
    primaryUse === 'host'
      ? [
          {
            name: 'pump',
            route: 'MyChargers',
            label: 'My Chargers',
            disabled: isGuest,
            image: pumpIcon,
          },
          {
            name: 'profile',
            route: 'MyAccount',
            label: 'My Account',
            image: profileIcon,
          },
        ]
      : primaryUse === 'driver' ||
        primaryUse === 'guest' ||
        primaryUse === '' ||
        !primaryUse
      ? [
          {
            name: 'history',
            route: 'TransactionHistory',
            label: 'Charging History',
            image: historyIcon,
          },
          {
            name: 'car',
            route: 'ChargerSelection',
            params: { 
              tabSelect: currentChargerSelectionTab, 
              key: currentChargerSelectionTab, 
              title: currentChargerSelectionTab 
            },
            label: 'Find a Charger',
            image: carIcon,
          },
          {
            name: 'profile',
            route: 'MyAccount',
            label: 'My Account',
            image: profileIcon,
          },
        ]
      : [];

  const footerButtons = buttons.map(
    ({ route, params, label, disabled, image }) => {
      const isActive = activeTab === route;
      return (
        <Pressable
          key={route}
          onPress={() => !disabled && handlePress(route, params)}
          disabled={disabled}
          style={{ 
            flex: 1, // Use flex instead of fixed width for equal distribution
            minWidth: 70, // Minimum width to prevent too much squishing
            maxWidth: isIPadDevice ? 120 : 110, // Maximum width for larger screens
            alignItems: 'center',
            paddingVertical: 4,
            paddingHorizontal: 2, // Small horizontal padding for spacing
          }}
        >
          <Animated.View
            style={[
              styles.iconWrapper,
              { transform: [{ scale: isActive ? scaleAnim : 1 }] },
            ]}
          >
            <View style={styles.iconContainer}>
              <Image
                source={image}
                style={[
                  ICON_SIZE,
                  { tintColor: isActive ? ACTIVE_TINT : DEFAULT_TINT },
                ]}
                resizeMode="contain"
              />
            </View>
            <Text
              {...accessibleTextProps.navigation}
              style={[
                styles.text, 
                { 
                  color: isActive ? ACTIVE_TINT : '#FFF',
                  fontSize: isIPadDevice ? responsiveDimensions.fontSize.small : 13,
                  marginTop: 2,
                }
              ]}
              ellipsizeMode="tail"
            >
              {label}
            </Text>
          </Animated.View>
        </Pressable>
      );
    }
  );

  return (
    <SafeAreaView style={{ backgroundColor: SOFT_BLACK }}>
      <Animated.View
        style={[
          styles.container,
          {
            width: isIPadDevice ? Math.min(width, 768) : (width < 430 ? width : 430),
            opacity: flashAnim,
            backgroundColor: SOFT_BLACK,
            transform: [{ scale: footerScaleAnim }],
            borderTopWidth: 1,
            borderTopColor: '#333',
            alignSelf: isIPadDevice ? 'center' : 'stretch',
            justifyContent: isIPadDevice ? 'center' : 'space-around',
            paddingHorizontal: isIPadDevice ? ipadAdjustments.paddingHorizontal : 24,
          },
        ]}
      >
        {footerButtons}
      </Animated.View>
    </SafeAreaView>
  );
};

export default Footer;

const styles = StyleSheet.create({
  container: {
    backgroundColor: SOFT_BLACK,
    paddingHorizontal: 4, // Reduced horizontal padding for more text space
    paddingVertical: 12, // Flexible vertical padding
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'flex-start', // Changed to flex-start so items align at top
    minHeight: height * 0.08, // Use minHeight to allow growth
    maxHeight: height * 0.12, // Maximum height to prevent excessive growth
    borderTopWidth: 1,
    borderTopColor: '#333',
  },
  iconWrapper: {
    alignItems: 'center',
    justifyContent: 'flex-start', // Changed to flex-start for consistent alignment
    paddingVertical: 2,
    width: '100%', // Take full width of parent for better text distribution
  },
  iconContainer: {
    height: 32, // Fixed height container for icon
    justifyContent: 'center', // Center icon vertically in its fixed container
    alignItems: 'center',
    marginBottom: 2, // Space between icon and text
  },
  text: {
    color: '#FFF',
    fontSize: 13, // Increased for better readability, maxFontSizeMultiplier handles scaling
    textAlign: 'center',
    fontWeight: '500',
    width: '100%', // Take full width for better wrapping
    paddingHorizontal: 2, // Small padding to prevent text touching edges
  },
});
